import React, { useRef, useLayoutEffect, useCallback } from "react";
import classNames from "classnames";

import setHelperText from "../helpers/setHelperText";
import { LoadingSkeleton } from "../../LoadingSkeleton";
import ReadOnlyContainer from "../../ReadOnlyContainer";
import { WrappedFieldInputProps, WrappedFieldMetaProps } from "redux-form";

// we should have a common props we extend from here
export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  label?: string;
  subLabel?: string;
  showSubLabel?: boolean;
  type?: string;
  input?: Partial<WrappedFieldInputProps>;
  inputExtraClasses?: string;
  helperText?: string;
  helperTextEnabled?: boolean;
  disabled?: boolean;
  showRequiredStar?: boolean;
  meta?: Partial<WrappedFieldMetaProps>;
  readOnly?: boolean;
}

export const TextField: React.FC<TextFieldProps> = (props: TextFieldProps): JSX.Element => {
  const {
    id,
    label,
    type,
    helperText,
    helperTextEnabled = true,
    subLabel,
    showSubLabel = false,
    disabled,
    input,
    showRequiredStar,
    inputExtraClasses,
    meta = { touched: false, error: "", active: false },
    readOnly = false,
    ...rest
  } = props;
  const { touched, error, active } = meta;
  let { value } = input;
  const { onFocus, onBlur } = input;

  const formElemRootClasses = classNames(
    "caprx-f-elem-root",
    { "caprx-f-elem-animate": active || value !== "" || readOnly },
    { "caprx-f-elem-error": touched && error },
    { "caprx-f-elem-disabled": disabled },
    { "caprx-h-display-none": type === "hidden" },
    { "caprx-f-elem-indent": (active && showSubLabel) || (showSubLabel && value !== "") },
    { "caprx-f-elem-helper-text-disabled": !helperTextEnabled },
  );

  // Currently React reflects the current input value for password fields to the "value" attribute. This is expected to be updated in React 18. For now, when the value changes or the input is blurred or focused, we manually remove the attribute. This does still result in a ,momentary flash in the DOM while the attribute is there, but it's the best we can do.
  const inputRef = useRef<HTMLInputElement | null>(null);
  useLayoutEffect(() => {
    if (type !== "password") return;

    setTimeout(() => {
      if (!inputRef.current) return;
      inputRef.current.removeAttribute("value");
    });
  }, [type, value]);

  const handleBlur = useCallback(
    (ev: React.FocusEvent) => {
      if (onBlur) onBlur(ev);
      if (type !== "password") return;

      setTimeout(() => {
        if (!inputRef.current) return;
        inputRef.current.removeAttribute("value");
      });
    },
    [type, onBlur],
  );

  const handleFocus = useCallback(
    (ev: React.FocusEvent) => {
      if (onFocus) onFocus(ev);
      if (type !== "password") return;

      setTimeout(() => {
        if (!inputRef.current) return;
        inputRef.current.removeAttribute("value");
      });
    },
    [type, onFocus],
  );

  const inputClasses = classNames("caprx-f-elem-input", inputExtraClasses, { "caprx-f-elem-read-only": readOnly });

  let body = (
    <input
      ref={inputRef}
      id={id}
      type={type}
      disabled={disabled}
      className={inputClasses}
      {...input}
      {...rest}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
  if (readOnly && type !== "password") {
    if (value === "" || value === undefined) {
      value = "N/A";
    }
    body = <ReadOnlyContainer {...rest} id={id} body={value} showSubLabel={showSubLabel} showLabel={label !== ""} />;
  }

  const subLabelElement = ((active && showSubLabel) || (showSubLabel && value !== "")) && showSubLabel && value !== "N/A" && (
    <label className="caprx-f-elem-sub-label">{subLabel}</label>
  );

  return (
    <LoadingSkeleton>
      <div className={formElemRootClasses}>
        {body}
        <label htmlFor={id} className="caprx-f-elem-label">
          {label}
          {showRequiredStar && <span className="required-star">*</span>}
        </label>
        {subLabelElement}
        {helperTextEnabled && <div className="caprx-f-elem-helper-text">{setHelperText(helperText, touched, error)}</div>}
      </div>
    </LoadingSkeleton>
  );
};

export default TextField;
